/**
 * External Imports
 */
import { FC, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { orderBy } from 'lodash';
import { format } from 'date-fns';
/**
 * Nexus Imports
 */
import { useAnalyticsApi } from '@shared-web-analytics/react/dist';

/**
 * Internal Imports
 */
import Table from '../../../shared/table';

import { RootState } from '../../../../redux/store';

import {
  logListClick,
  logRoundProfile
} from '../../../../utils/analyticsHelpers';
import { useDebounceValue } from '../../../../utils/debouncers';
import { date, yearsOfService } from '../../../../utils/helpers';

/**
 * Global Type Definition Imports
 */
import { Employee, RoundHistory } from '../../../../config/interfaces';

/**
 * Style Imports
 */
import {
  CellContainer,
  StyledLabel,
  StyledNeuTableRow
} from '../../../shared/table/Table.styles';
import {
  EmployeeInfoContainer,
  IconDiv,
  OnShiftIcon,
  StartDateContainer
} from './CSCListTable.styles';

/**
 * Static File Imports
 */
import onShiftIcon from '../../../../assets/images/calendar_check_icon.png';

interface ICSCListTableProps {
  csrnFilter: string;
  csrnRounds: Array<Employee & RoundHistory>;
  adhocRounds: Array<Employee & RoundHistory>;
  isLoading: boolean;
  searchTerm: string;
  selectedDeptsFromStore: string[];
}

const CSCListTable: FC<ICSCListTableProps> = ({
  csrnFilter,
  csrnRounds,
  adhocRounds,
  isLoading,
  searchTerm,
  selectedDeptsFromStore
}) => {
  const [sortKey, setSortKey] = useState<string>('lastRoundDate');
  const [sortDir, setSortDir] = useState<'desc' | 'asc'>('asc');
  const [active, setActive] = useState<string>('Last Round');

  const { logTrackingEvent } = useAnalyticsApi();

  const dbSearchTerm = useDebounceValue(searchTerm, 250);

  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { '*': view } = useParams();

  // Commented out instead of removing in case we need to re-add profile pics later
  // const {
  //   data: sasTokenData,
  //   isError: isGetSassTokenError,
  //   isFetching: isGetSassTokenFetching
  //   isLoading: isGetSassTokenLoading
  // } = useGetSassTokenQuery();

  const openEmployeeProfile = (employee: Employee) => {
    logTrackingEvent([
      ...logListClick('table', view),
      ...logRoundProfile('csrn')
    ]);
    navigate('/support/profile/csc', {
      state: {
        from: pathname,
        employeeId: employee.hcaid,
        mode: undefined
      }
    });
  };

  const handleSort = (column: string) => {
    let key;
    switch (column) {
      case 'Employee':
        key = 'fullName';
        break;
      case 'Department':
        key = 'dept';
        break;
      case 'RN Experience':
        key = 'rnExperienceDate';
        break;
      case 'Dept Start':
        key = 'effectiveStartDate';
        break;
      case 'Rounded By':
        key = 'lastRoundedBy';
        break;
      case 'Last Round':
        key = 'lastRoundDate';
        break;
      default:
        key = '';
    }
    if (key === sortKey && sortDir === 'asc') {
      setSortDir('desc');
    } else {
      setSortDir('asc');
    }
    setActive(column);
    setSortKey(key);
  };

  const filteredCSCRounds = useMemo(() => {
    let employeesToShow;
    switch (csrnFilter) {
      case 'no-round':
        employeesToShow = csrnRounds.filter(
          employee =>
            !employee?.lastRoundDate ||
            (employee?.lastRoundDate &&
              date.isOverInDays(employee.lastRoundDate, 30))
        );
        break;
      case 'recently-completed':
        employeesToShow = [
          ...csrnRounds.filter(
            employee =>
              employee?.lastRoundDate &&
              !date.isOverInWeeks(employee.lastRoundDate, 2)
          ),
          ...adhocRounds
        ];
        break;
      case 'new-nurses':
        employeesToShow = csrnRounds.filter(employee =>
          employee.subjectClassifications?.includes('NewNurse')
        );
        break;
      case 'new-to-hca-nurses':
        employeesToShow = csrnRounds.filter(employee =>
          employee.subjectClassifications?.includes('NewToHCA')
        );
        break;
      case 'new-to-dept-nurses':
        employeesToShow = csrnRounds.filter(employee =>
          employee.subjectClassifications?.includes('NewToDepartment')
        );
        break;
      case 'travel-nurses':
        employeesToShow = csrnRounds.filter(() => false);
        break;
      default:
        employeesToShow = csrnRounds;
    }

    const q = new RegExp(dbSearchTerm.trim().replace(/\\/g, ''), 'i');
    return employeesToShow.filter(
      employee =>
        `${employee.firstName} ${employee.lastName}`.match(q) ||
        employee.hcaid.match(q) ||
        `${employee.lastName} ${employee.firstName}`.match(q)
    );
  }, [csrnFilter, csrnRounds, dbSearchTerm, selectedDeptsFromStore]);

  const sortedCSCRounds = useMemo(() => {
    const filteredCSCRoundsCopy = [...filteredCSCRounds];
    const orderBySortDir = sortDir === 'asc' ? 'asc' : 'desc';
    return filteredCSCRoundsCopy.length > 0
      ? orderBy(
          filteredCSCRoundsCopy,
          [round => (round as any)[sortKey]?.toLowerCase() || ''],
          [orderBySortDir]
        )
      : filteredCSCRoundsCopy;
  }, [filteredCSCRounds, sortDir, sortKey]);

  const isTableEmpty = useMemo(
    () => !dbSearchTerm && sortedCSCRounds.length === 0,
    [dbSearchTerm, sortedCSCRounds]
  );

  const isTableSearchEmpty = useMemo(
    () => dbSearchTerm && sortedCSCRounds.length === 0,
    [dbSearchTerm, sortedCSCRounds]
  );

  const headers: JSX.Element[] = [
    <CellContainer
      key="Employee"
      id="CSC-Table-Employee-Column"
      unit="column"
      colWidth={23}
      direction={!(active === 'Employee') ? 'none' : sortDir}
      sort
    >
      <span className="text-clamp text-clamp__1 break-all">Employee</span>
    </CellContainer>,
    <CellContainer
      key="Department"
      id="CSC-Table-Department-Column"
      unit="column"
      colWidth={20}
      direction={!(active === 'Department') ? 'none' : sortDir}
      sort
    >
      <span className="text-clamp text-clamp__1 break-all">Department</span>
    </CellContainer>,
    <CellContainer
      key="RN Experience"
      id="CSC-Table-RN-Experience-Column"
      data-testid="CSC-Table-RN-Experience-Column-Heading"
      unit="column"
      colWidth={14}
      direction={
        !(active === 'RN Experience')
          ? 'none'
          : sortDir === 'asc'
          ? 'desc'
          : 'asc'
      }
      sort
    >
      <span className="text-clamp text-clamp__1 break-all">RN Experience</span>
    </CellContainer>,
    <CellContainer
      key="Dept Start"
      id="CSC-Table-Dept-Start-Column"
      unit="column"
      colWidth={14}
      direction={!(active === 'Dept Start') ? 'none' : sortDir}
      sort
    >
      <span className="text-clamp text-clamp__1 break-all">Dept Start</span>
    </CellContainer>,
    <CellContainer
      key="Rounded By"
      id="CSC-Table-Rounded-By-Column"
      unit="column"
      colWidth={17}
      direction={!(active === 'Rounded By') ? 'none' : sortDir}
      sort
    >
      <span className="text-clamp text-clamp__1 break-all">Rounded By</span>
    </CellContainer>,
    <CellContainer
      key="Last Round"
      id="CSC-Table-Last-Round-Column"
      unit="column"
      colWidth={12}
      direction={!(active === 'Last Round') ? 'none' : sortDir}
      sort
    >
      <span className="text-clamp text-clamp__1 break-all">Last Round</span>
    </CellContainer>
  ];

  const rows = sortedCSCRounds.map(
    (employee: Employee & RoundHistory, i: number) => (
      <StyledNeuTableRow
        // eslint-disable-next-line react/no-array-index-key
        key={`CSC-List-Row-${i}`}
        id={`CSC-List-Row-${employee.hcaid}`}
        data-testid={`CSC-List-Row-${employee.hcaid}`}
        unit="row"
        size="default"
        role="button"
        onClick={() => openEmployeeProfile(employee)}
      >
        <CellContainer unit="column" colWidth={23}>
          <EmployeeInfoContainer className="h-100 px-0">
            <IconDiv>
              {employee.onShift && (
                <OnShiftIcon src={onShiftIcon} alt="On Shift Icon" />
              )}
            </IconDiv>
            {/* <AvatarContainer
              className="neu-text-gray-60"
              style={{ height: '40px', width: '40px' }}
            >
              {sasTokenData ? (
                <EmployeeAvatar
                  src={
                    handleBlobPicUrl(
                      employee.pictureUrl,
                      sasTokenData.sasToken
                    ) ??
                    userProfilePicUrl(employee.hcaid, sasTokenData.sasToken)
                  }
                  alt="Employee Image"
                  onError={(e: any) => {
                    e.currentTarget.src = defaultImage;
                    return null;
                  }}
                />
              ) : (
                <EmployeeAvatar src={defaultImage} alt="Default Image" />
              )}
            </AvatarContainer> */}
            <div>
              <StyledLabel className="neu-text-blue-70 bold">
                {employee.firstName} {employee.lastName}
              </StyledLabel>
              <StyledLabel>{employee.hcaid}</StyledLabel>
            </div>
          </EmployeeInfoContainer>
        </CellContainer>
        <CellContainer unit="column" colWidth={20}>
          <StyledLabel className="neu-text-gray-90">
            {employee.adHoc ? employee.employeeDept : employee.dept}
          </StyledLabel>
        </CellContainer>
        <CellContainer
          unit="column"
          colWidth={14}
          style={{ textAlign: 'left' }}
        >
          <StartDateContainer>
            {employee.rnExperienceDate && employee.rnExperienceDate !== 'NULL'
              ? yearsOfService(employee.rnExperienceDate)
              : 'Unknown'}
          </StartDateContainer>
        </CellContainer>
        <CellContainer unit="column" colWidth={14}>
          <StyledLabel className="d-flex">
            {employee.effectiveStartDate &&
            employee.effectiveStartDate !== 'NULL'
              ? date.datePrettyString(employee.effectiveStartDate)
              : 'Unknown'}
          </StyledLabel>
        </CellContainer>{' '}
        <CellContainer unit="column" colWidth={17}>
          <StyledLabel>
            {employee.lastRoundDate &&
            employee.lastRoundedBy &&
            employee.lastRoundedBy !== 'Unknown'
              ? employee.lastRoundedBy
              : employee.lastRoundDate &&
                employee.lastRoundedBy &&
                employee.lastRoundedBy === 'Unknown'
              ? 'Unknown'
              : ''}
          </StyledLabel>
        </CellContainer>
        <CellContainer unit="column" colWidth={12}>
          <StyledLabel>
            {employee.lastRoundedBy &&
            employee.lastRoundedBy !== 'Unknown' &&
            (!employee.lastRoundDate ||
              (employee.lastRoundDate &&
                !date.isOrbitTimestamp(employee.lastRoundDate)))
              ? 'Unknown'
              : employee.lastRoundedBy && employee.lastRoundDate
              ? format(date.parseDate(employee.lastRoundDate), 'MM/dd/yyyy') // make unknown if name and no date
              : ''}
          </StyledLabel>
        </CellContainer>
      </StyledNeuTableRow>
    )
  );

  return (
    <Table
      customClass="csc-list-table"
      headers={headers}
      rows={rows}
      isTableEmpty={isTableEmpty}
      isTableEmptySearch={isTableSearchEmpty}
      loading={isLoading}
      type="cscList"
      onSort={(e: any) => handleSort(e.detail.label)}
    />
  );
};

const mapReduxStateToProps = (state: RootState) => ({
  isLoading: state.EmployeeReducer.isLoadingSubjectsToRoundForList,
  selectedDeptsFromStore: state.EmployeeReducer.selectedDepts
});

export default connect(mapReduxStateToProps)(CSCListTable);
